import React, { useEffect, useState } from "react";
import axios from "axios";
import shared from "../styles/shared.module.css";
import styles from "../styles/AdminVeiculos.module.css";
import { CheckCircle, XCircle, RotateCcw, Trash2 } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

type Veiculo = {
  id: number;
  modelo: string;
  placa: string;
  cor: string;
  capacidade: number;
  status: "PENDENTE" | "APROVADO" | "REJEITADO";
  empresaNome: string;
  fotoUrl?: string;
  livreteUrl?: string;
  seguroUrl?: string;
  licencaRnaatUrl?: string;
  seguroOcupantesUrl?: string;
  motivoReprovacao?: string;

  // LIVRETE
  numeroMatricula?: string;
  dataEmissao?: string;
  dataFabricacao?: string;
  numeroChassi?: string;

  // SEGURO
  numeroApolice?: string;
  nomeAssegurador?: string;
  nomeSeguradora?: string;
  dataEmissaoSeguro?: string;
  dataTerminoSeguro?: string;

  // LICENÇA RNAAT
  numeroLicencaRnaat?: string;

  // SEGURO OCUPANTES
  numeroApoliceOcupantes?: string;
  nomeAsseguradorOcupantes?: string;
  nomeSeguradoraOcupantes?: string;
  dataEmissaoSeguroOcupantes?: string;
  dataTerminoSeguroOcupantes?: string;
};

const AdminVeiculos = () => {
  const [veiculosPendentes, setVeiculosPendentes] = useState<Veiculo[]>([]);
  const [veiculosAprovados, setVeiculosAprovados] = useState<Veiculo[]>([]);
  const [abaAtiva, setAbaAtiva] = useState<"pendentes" | "aprovados">("pendentes");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [veiculoSelecionado, setVeiculoSelecionado] = useState<number | null>(null);
  const [loadingPDF, setLoadingPDF] = useState(false);

  const BASE_URL = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchVeiculos = async () => {
    try {
      const [pendentesRes, aprovadosRes] = await Promise.all([
        axios.get(`${BASE_URL}/admins/veiculos/pendentes`, { headers }),
        axios.get(`${BASE_URL}/admins/veiculos/aprovados`, { headers }),
      ]);
      setVeiculosPendentes(pendentesRes.data);
      setVeiculosAprovados(aprovadosRes.data);
    } catch (err) {
      console.error("Erro ao buscar veículos:", err);
    }
  };

  useEffect(() => {
    fetchVeiculos();
  }, []);

  const atualizarStatus = async (id: number, status: Veiculo["status"], motivoReprovacao?: string) => {
    try {
      await axios.put(`${BASE_URL}/admins/veiculos/${id}/status`, { status, motivoReprovacao }, { headers });
      fetchVeiculos();
    } catch (err) {
      console.error("Erro ao atualizar status:", err);
    }
  };

  const handleDeletar = async (id: number) => {
    if (!window.confirm("Tem certeza que deseja excluir este veículo?")) return;
    try {
      await axios.delete(`${BASE_URL}/admins/veiculos/${id}`, { headers });
      fetchVeiculos();
    } catch (err) {
      console.error("Erro ao deletar veículo:", err);
    }
  };

  const toggleExpand = (id: number) => setExpandedId(prev => (prev === id ? null : id));
  const lista = abaAtiva === "pendentes" ? veiculosPendentes : veiculosAprovados;

  const DropdownSection = ({
    title,
    url,
    infos
  }: {
    title: string;
    url?: string;
    infos?: Record<string, string | undefined>;
  }) => {
    const [open, setOpen] = useState(false);

    if (!url && (!infos || Object.values(infos).every(v => !v))) return null;

    return (
      <div className={styles.dropdownSection}>
        <div className={styles.dropdownHeader} onClick={() => setOpen(!open)}>
          <strong>{title}</strong>
          <span>{open ? "▲" : "▼"}</span>
        </div>

        {open && (
          <div className={styles.dropdownContent}>
            <div className={styles.dropdownGrid}>
              {infos && (
                <div className={styles.infoTable}>
                  {Object.entries(infos).map(
                    ([key, val]) =>
                      val && (
                        <p key={key}>
                          <strong>{key.replace(/([A-Z])/g, " $1")}:</strong> {val}
                        </p>
                      )
                  )}
                </div>
              )}

              {url && (
                <div className={styles.thumbWrapper}>
                  <a
                    href={BASE_URL + url}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.fileLink}
                  >
                    <img
                      src={BASE_URL + url}
                      alt={title}
                      className={styles.thumbLarge}
                      loading="lazy"
                    />
                  </a>
                  <p className={styles.clickHint}>Clique para ampliar</p>
                </div>
              )}

            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={shared.container}>
      <h1>Gerenciar Veículos</h1>

      <div className={styles.tabs}>
        {["pendentes", "aprovados"].map(tab => (
          <button
            key={tab}
            className={`${styles.tabButton} ${abaAtiva === tab ? styles.tabActive : ""}`}
            onClick={() => setAbaAtiva(tab as "pendentes" | "aprovados")}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {lista.length === 0 ? (
        <p>Nenhum veículo {abaAtiva}.</p>
      ) : (
        lista.map(veiculo => {
          const isExpanded = expandedId === veiculo.id;
          return (
            <div key={veiculo.id} className={`${styles.cardVeiculo} ${isExpanded ? styles.expanded : styles.collapsed}`}>
              <div className={styles.summary} onClick={() => toggleExpand(veiculo.id)}>
                <strong>{veiculo.modelo} - {veiculo.placa}</strong>
                <span
                  className={`${styles.status} ${veiculo.status === "PENDENTE" ? styles.statusPendente :
                    veiculo.status === "APROVADO" ? styles.statusAprovada : styles.statusRejeitada}`}
                >
                  {veiculo.status === "PENDENTE" ? "⏳ Pendente" : veiculo.status === "APROVADO" ? "✅ Aprovado" : "❌ Rejeitado"}
                </span>
                <button className={styles.toggleBtn}>{isExpanded ? "▲" : "▼"}</button>
              </div>

              {isExpanded && (
                <div className={styles.details}>
                  <button
                    className={styles.exportButton}
                    onClick={() => handleExportPDF(veiculo, setLoadingPDF)}
                    disabled={loadingPDF}
                  >
                    {loadingPDF ? "⏳ Gerando PDF..." : "📝 Exportar PDF"}
                  </button>

                  <div className={styles.detailsMain}>
                    {/* Infos principais */}
                    <div className={styles.vehicleInfos}>
                      <p><strong>Empresa:</strong> {veiculo.empresaNome}</p>
                      <p><strong>Placa:</strong> {veiculo.placa}</p>
                      <p><strong>Cor:</strong> {veiculo.cor}</p>
                      <p><strong>Capacidade:</strong> {veiculo.capacidade}</p>
                    </div>
                    {/* Foto do veículo */}
                    {veiculo.fotoUrl && (
                      <div className={styles.vehiclePhoto}>
                        <a
                          href={BASE_URL + veiculo.fotoUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <img
                            src={BASE_URL + veiculo.fotoUrl}
                            alt="Foto do veículo"
                            className={styles.thumbLarge}
                            loading="lazy"
                          />
                        </a>
                        <p className={styles.clickHint}>Clique para ampliar</p>
                      </div>
                    )}
                  </div>

                  <DropdownSection
                    title="Livrete"
                    url={veiculo.livreteUrl}
                    infos={{
                      "Número Matrícula": veiculo.numeroMatricula,
                      "Número Chassi": veiculo.numeroChassi,
                      "Data Emissão": veiculo.dataEmissao,
                      "Data Fabricação": veiculo.dataFabricacao
                    }}
                  />

                  <DropdownSection
                    title="Seguro do Veículo"
                    url={veiculo.seguroUrl}
                    infos={{
                      "Número Apólice": veiculo.numeroApolice,
                      "Nome Assegurador": veiculo.nomeAssegurador,
                      "Nome Seguradora": veiculo.nomeSeguradora,
                      "Data Emissão": veiculo.dataEmissaoSeguro,
                      "Data Término": veiculo.dataTerminoSeguro
                    }}
                  />

                  <DropdownSection
                    title="Licença RNAAT"
                    url={veiculo.licencaRnaatUrl}
                    infos={{
                      "Número Licença": veiculo.numeroLicencaRnaat
                    }}
                  />

                  <DropdownSection
                    title="Seguro Ocupantes"
                    url={veiculo.seguroOcupantesUrl}
                    infos={{
                      "Número Apólice": veiculo.numeroApoliceOcupantes,
                      "Nome Assegurador": veiculo.nomeAsseguradorOcupantes,
                      "Nome Seguradora": veiculo.nomeSeguradoraOcupantes,
                      "Data Emissão": veiculo.dataEmissaoSeguroOcupantes,
                      "Data Término": veiculo.dataTerminoSeguroOcupantes
                    }}
                  />

                  <div className={styles.actions}>
                    {veiculo.status === "PENDENTE" && (
                      <>
                        <button className={styles.aprovarButton} onClick={() => atualizarStatus(veiculo.id, "APROVADO")}>
                          <CheckCircle size={16} /> Aprovar
                        </button>
                        <button className={styles.rejeitarButton} onClick={() => { setVeiculoSelecionado(veiculo.id); setShowModal(true); }}>
                          <XCircle size={16} /> Rejeitar
                        </button>
                      </>
                    )}

                    {(veiculo.status === "APROVADO" || veiculo.status === "REJEITADO") && (
                      <button className={styles.desaprovarButton} onClick={() => atualizarStatus(veiculo.id, "PENDENTE")}>
                        <RotateCcw size={16} /> Voltar para Pendente
                      </button>
                    )}

                    <button className={styles.deleteButton} onClick={() => handleDeletar(veiculo.id)}>
                      <Trash2 size={16} /> Deletar
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}

      {showModal && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalContent}>
            <h2>Motivo da Reprovação</h2>
            <textarea className={styles.textarea} value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Descreva o motivo..." />
            <div className={styles.modalActions}>
              <button className={styles.confirmButton} onClick={() => {
                if (veiculoSelecionado) atualizarStatus(veiculoSelecionado, "REJEITADO", motivo);
                setMotivo(""); setShowModal(false);
              }}>Confirmar</button>
              <button className={styles.cancelButton} onClick={() => { setMotivo(""); setShowModal(false); }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVeiculos;


//PDF
export const handleExportPDF = async (
  veiculo: Veiculo,
  setLoadingPDF: (val: boolean) => void
) => {
  try {
    setLoadingPDF(true); // inicia loading

    const BASE_URL = process.env.REACT_APP_API_URL;
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    let y = 20;

    const drawSeparator = () => {
      doc.setDrawColor(180);
      doc.setLineWidth(0.3);
      doc.line(margin, y, pageWidth - margin, y);
      y += 8;
    };

    // Cabeçalho
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Relatório do Veículo", pageWidth / 2, y, { align: "center" });
    y += 12;
    drawSeparator();

    // Função auxiliar de texto
    const addTextField = (label: string, value?: string) => {
      if (!value) return;
      const labelX = margin;
      const maxWidthLabel = 60;
      const valueX = labelX + maxWidthLabel + 5;
      const maxWidthValue = pageWidth - valueX - margin;
      doc.setFontSize(11);
      const splittedTitle = doc.splitTextToSize(`${label}:`, maxWidthLabel);
      doc.setFont("helvetica", "bold");
      doc.text(splittedTitle, labelX, y);
      const titleHeight = splittedTitle.length * 4;
      doc.setFont("helvetica", "normal");
      doc.text(value, valueX, y + (titleHeight > 6 ? titleHeight - 6 : 0), {
        maxWidth: maxWidthValue,
      });
      y += Math.max(titleHeight, 6) + 6;
    };

    // Infos principais
    addTextField("Modelo", veiculo.modelo);
    addTextField("Placa", veiculo.placa);
    addTextField("Empresa", veiculo.empresaNome);
    addTextField("Cor", veiculo.cor);
    addTextField("Capacidade", `${veiculo.capacidade}`);
    drawSeparator();

    // Infos extras em tabela
    const infos: Record<string, string | undefined> = {
      "Número Matrícula": veiculo.numeroMatricula,
      "Número Chassi": veiculo.numeroChassi,
      "Data Emissão": veiculo.dataEmissao,
      "Data Fabricação": veiculo.dataFabricacao,
      "Número Apólice": veiculo.numeroApolice,
      "Nome Assegurador": veiculo.nomeAssegurador,
      "Nome Seguradora": veiculo.nomeSeguradora,
      "Data Emissão Seguro": veiculo.dataEmissaoSeguro,
      "Data Término Seguro": veiculo.dataTerminoSeguro,
      "Número Licença RNAAT": veiculo.numeroLicencaRnaat,
      "Número Apólice Ocupantes": veiculo.numeroApoliceOcupantes,
      "Nome Assegurador Ocupantes": veiculo.nomeAsseguradorOcupantes,
      "Nome Seguradora Ocupantes": veiculo.nomeSeguradoraOcupantes,
      "Data Emissão Seguro Ocupantes": veiculo.dataEmissaoSeguroOcupantes,
      "Data Término Seguro Ocupantes": veiculo.dataTerminoSeguroOcupantes,
    };

    const tableData: [string, string][] = Object.entries(infos)
      .filter(([_, val]) => val)
      .map(([key, val]) => [key, val as string]);

    if (tableData.length > 0) {
      autoTable(doc, {
        startY: y,
        head: [["Campo", "Valor"]],
        body: tableData,
        theme: "grid",
        styles: { fontSize: 10 },
        headStyles: { fillColor: [200, 200, 200] },
        margin: { left: margin, right: margin },
        columnStyles: { 0: { cellWidth: 60 }, 1: { cellWidth: pageWidth - margin * 2 - 60 } },
      });
      y = doc.lastAutoTable?.finalY + 10 || y;
    }

    drawSeparator();

    // Pré-carrega imagens em paralelo
    const imageInfos: { label: string; url?: string }[] = [
      { label: "Foto do Veículo", url: veiculo.fotoUrl },
      { label: "Livrete", url: veiculo.livreteUrl },
      { label: "Seguro do Veículo", url: veiculo.seguroUrl },
      { label: "Licença RNAAT", url: veiculo.licencaRnaatUrl },
      { label: "Seguro Ocupantes", url: veiculo.seguroOcupantesUrl },
    ];

    const loadImage = (url: string) =>
      new Promise<string>((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = BASE_URL + url;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx?.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = (err) => reject(err);
      });

    const images = await Promise.all(
      imageInfos.map((info) =>
        info.url ? loadImage(info.url).then((dataURL) => ({ ...info, dataURL })).catch(() => null) : null
      )
    );

    // Adiciona imagens carregadas
    for (const img of images) {
      if (!img) continue;
      doc.addPage();
      y = 20;
      doc.setFont("helvetica", "bold");
      doc.text(img.label, pageWidth / 2, y, { align: "center" });
      y += 10;
      const maxWidth = pageWidth - margin * 2;
      const imgObj = new Image();
      imgObj.src = img.dataURL!;
      await new Promise<void>((res) => {
        imgObj.onload = () => res();
      });
      const imgHeight = maxWidth * (imgObj.height / imgObj.width);
      doc.addImage(img.dataURL!, "PNG", margin, y, maxWidth, imgHeight);
    }

    doc.save(`${veiculo.modelo}-${veiculo.placa}.pdf`);
  } finally {
    setLoadingPDF(false); // finaliza loading
  }
};

